/**
 * WEAR CLOVERSE — Quick View (fast, custom modal)
 *
 * - Prefetches the product's quick-view HTML on card hover and caches it, so
 *   clicking the eye opens instantly (skeleton only if data isn't ready yet).
 * - Fetches {productUrl}?view=quickview (templates/product.quickview.liquid,
 *   {% layout none %}) — a small, cacheable payload with all Liquid data.
 * - Custom 1000px modal: gallery + thumbnails, variant switching, quantity,
 *   Add to Cart (Horizon product-form-component), wishlist, accordions.
 * - ESC / overlay / close button; scroll lock + restore; focus handling.
 */
import { ThemeEvents } from '@theme/events';

/** @type {Map<string, Promise<Element|null>>} */
const cache = new Map();

const SKELETON = `
  <div class="cqv cqv--skeleton" aria-hidden="true">
    <div class="cqv__gallery">
      <div class="cqv__main cqv-skel"></div>
      <div class="cqv__thumbs">
        <span class="cqv__thumb cqv-skel"></span><span class="cqv__thumb cqv-skel"></span>
        <span class="cqv__thumb cqv-skel"></span><span class="cqv__thumb cqv-skel"></span>
      </div>
    </div>
    <div class="cqv__info">
      <div class="cqv-skel cqv-skel--line" style="width:40%"></div>
      <div class="cqv-skel cqv-skel--line" style="width:80%;height:1.6rem"></div>
      <div class="cqv-skel cqv-skel--line" style="width:30%;height:1.4rem"></div>
      <div class="cqv-skel cqv-skel--line" style="width:100%"></div>
      <div class="cqv-skel cqv-skel--line" style="width:90%"></div>
      <div class="cqv-skel cqv-skel--block" style="margin-top:auto"></div>
      <div class="cqv-skel cqv-skel--block"></div>
    </div>
  </div>`;

let modal = null;
let lastFocus = null;
let scrollY = 0;

/* ---------------------------------------------------------------- prefetch */

function quickViewUrl(productUrl) {
  const url = new URL(productUrl, window.location.origin);
  url.searchParams.set('view', 'quickview');
  return url.toString();
}

function prefetch(productUrl) {
  if (!productUrl || cache.has(productUrl)) return cache.get(productUrl);
  const promise = fetch(quickViewUrl(productUrl), { headers: { 'X-Requested-With': 'quickview' } })
    .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
    .then((html) => new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cqv]'))
    .catch(() => null);
  cache.set(productUrl, promise);
  return promise;
}

/* ------------------------------------------------------------------- modal */

function buildModal() {
  const el = document.createElement('div');
  el.className = 'cqv-modal';
  el.setAttribute('data-cqv-modal', '');
  el.hidden = true;
  el.innerHTML = `
    <div class="cqv-modal__overlay" data-cqv-close></div>
    <div class="cqv-modal__dialog" role="dialog" aria-modal="true" aria-label="Product quick view">
      <button class="cqv-modal__close" data-cqv-close aria-label="Close quick view" type="button">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
      </button>
      <div class="cqv-modal__content" data-cqv-content></div>
    </div>`;
  document.body.appendChild(el);

  el.addEventListener('click', (e) => {
    if (e.target instanceof Element && e.target.closest('[data-cqv-close]')) closeModal();
  });
  return el;
}

function openModal() {
  if (!modal) modal = buildModal();
  if (!modal.hidden) return;
  lastFocus = document.activeElement;
  scrollY = window.scrollY;
  document.body.style.top = `-${scrollY}px`;
  document.documentElement.classList.add('cqv-lock');
  modal.hidden = false;
  requestAnimationFrame(() => modal.classList.add('is-open'));
  modal.querySelector('.cqv-modal__close')?.focus();
}

function closeModal() {
  if (!modal || modal.hidden) return;
  modal.classList.remove('is-open');
  const finish = () => {
    modal.hidden = true;
    document.documentElement.classList.remove('cqv-lock');
    document.body.style.top = '';
    window.scrollTo(0, scrollY);
    if (lastFocus instanceof HTMLElement) lastFocus.focus();
  };
  const dialog = modal.querySelector('.cqv-modal__dialog');
  if (dialog) {
    let done = false;
    const once = () => { if (done) return; done = true; finish(); };
    dialog.addEventListener('transitionend', once, { once: true });
    setTimeout(once, 260);
  } else finish();
}

async function openQuickView(productUrl) {
  if (!productUrl) return;
  openModal();
  const content = modal.querySelector('[data-cqv-content]');
  content.innerHTML = SKELETON;

  const node = await prefetch(productUrl);
  if (modal.hidden) return; // closed while loading
  if (!node) { window.location.assign(productUrl); return; }

  content.innerHTML = '';
  const clone = /** @type {Element} */ (node.cloneNode(true));
  content.appendChild(clone);
  requestAnimationFrame(() => clone.classList.add('cqv--in'));
  wire(clone);
}

/* --------------------------------------------------------- content wiring */

function formatMoney(cents, format) {
  const value = (cents / 100).toFixed(2);
  const parts = value.split('.');
  const withCommas = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const noDecimals = withCommas;
  const withDecimals = `${withCommas}.${parts[1]}`;
  return (format || '${{amount}}')
    .replace(/\{\{\s*amount\s*\}\}/g, withDecimals)
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withDecimals)
    .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimals);
}

function wire(root) {
  let data = { variants: [], optionCount: 1 };
  try { data = JSON.parse(root.querySelector('[data-cqv-json]')?.textContent || '{}'); } catch (e) { /* noop */ }
  const format = root.getAttribute('data-money-format') || document.querySelector('[data-money-format]')?.getAttribute('data-money-format') || '';

  const mainImg = root.querySelector('[data-cqv-main]');
  const priceEl = root.querySelector('[data-cqv-price]');
  const compareEl = root.querySelector('[data-cqv-compare]');
  const discountEl = root.querySelector('[data-cqv-discount]');
  const variantInput = root.querySelector('[data-cqv-variant-id]');
  const addBtn = root.querySelector('[data-cqv-add]');
  const addText = root.querySelector('[data-cqv-add-text]');
  const qtyValue = root.querySelector('[data-cqv-qty-value]');
  const qtyDisplay = root.querySelector('[data-cqv-qty-display]');

  // ---- gallery: thumbnails + prev/next arrows
  // The thumbnail rail is the single source of truth for the image list, so the
  // arrows page through the same order and the two controls can never disagree.
  const thumbs = [...root.querySelectorAll('[data-cqv-thumb]')];

  const showThumb = (thumb) => {
    if (!thumb) return;
    if (mainImg && thumb.dataset.full) mainImg.src = thumb.dataset.full;
    thumbs.forEach((t) => t.classList.toggle('is-active', t === thumb));
  };

  thumbs.forEach((thumb) => thumb.addEventListener('click', () => showThumb(thumb)));

  // Wraps at both ends: with two or three images, a dead arrow at the edge is
  // more annoying than a loop.
  const step = (delta) => {
    if (thumbs.length < 2) return;
    const current = thumbs.findIndex((t) => t.classList.contains('is-active'));
    const index = (Math.max(current, 0) + delta + thumbs.length) % thumbs.length;
    showThumb(thumbs[index]);
  };

  root.querySelectorAll('[data-cqv-nav]').forEach((btn) => {
    btn.addEventListener('click', () => step(Number(btn.getAttribute('data-cqv-nav'))));
  });

  // ---- variant switching
  const optionInputs = [...root.querySelectorAll('[data-cqv-option-input]')];

  // Selection indexed by option position, so a not-yet-chosen option leaves a
  // hole rather than shifting every later option into the wrong slot.
  const currentSelection = () => {
    const arr = new Array(data.optionCount || 1).fill(null);
    for (const input of optionInputs) {
      if (input.checked) arr[Number(input.dataset.position) - 1] = input.value;
    }
    return arr;
  };

  const matchesSelection = (variant, sel) =>
    Array.isArray(variant.options) && sel.every((val, i) => val == null || variant.options[i] === val);

  const selectedOptions = () => currentSelection();

  const matchVariant = (sel) => data.variants.find((v) => matchesSelection(v, sel));

  // Grey out any size that has nothing left, given whatever else is chosen.
  // This is what makes a sold-out size visible instead of failing at add time.
  const refreshAvailability = () => {
    const sel = currentSelection();
    for (const input of optionInputs) {
      const candidate = sel.slice();
      candidate[Number(input.dataset.position) - 1] = input.value;
      const sellable = data.variants.some((v) => v.available && matchesSelection(v, candidate));
      input.disabled = !sellable;
      input.closest('.cqv__opt-value')?.classList.toggle('is-unavailable', !sellable);
    }
  };

  const applyVariant = (variant) => {
    if (!variant) return;
    if (variantInput) variantInput.value = variant.id;
    if (priceEl) priceEl.textContent = formatMoney(variant.price, format);
    const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
    if (compareEl) {
      compareEl.hidden = !onSale;
      if (onSale) compareEl.textContent = formatMoney(variant.compare_at_price, format);
    }
    if (discountEl) {
      discountEl.hidden = !onSale;
      if (onSale) discountEl.textContent = `-${Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100)}%`;
    }
    if (addBtn) addBtn.disabled = !variant.available;
    if (addText) addText.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
    const src = variant.featured_image && variant.featured_image.src;
    if (mainImg && src) {
      mainImg.src = src;
      const mediaId = variant.featured_media && variant.featured_media.id;
      if (mediaId) {
        thumbs.forEach((t) => t.classList.toggle('is-active', String(t.dataset.mediaId) === String(mediaId)));
      }
    }
  };

  optionInputs.forEach((input) => {
    input.addEventListener('change', () => {
      applyVariant(matchVariant(currentSelection()));
      refreshAvailability();
    });
  });
  refreshAvailability();

  // ---- quantity
  // product-form.js reads the quantity from refs.quantitySelector.getValue(),
  // then falls back to the component's data-quantity-default. This modal has no
  // quantity-selector component, so without the attribute every add posts 1
  // regardless of what the stepper shows. Keep the attribute in step with it.
  const formComponent = root.querySelector('product-form-component');
  const syncQty = () => {
    if (qtyValue) qtyValue.value = String(qty);
    if (qtyDisplay) qtyDisplay.textContent = String(qty);
    if (formComponent) formComponent.dataset.quantityDefault = String(qty);
  };

  let qty = 1;
  syncQty();

  root.querySelectorAll('[data-cqv-qty]').forEach((btn) => {
    btn.addEventListener('click', () => {
      qty = Math.max(1, qty + Number(btn.dataset.cqvQty));
      syncQty();
    });
  });

}

/* ----------------------------------------------------------- global events */

document.addEventListener('pointerover', (e) => {
  const target = e.target;
  if (!(target instanceof Element)) return;
  const card = target.closest('.product-card, .cloverse-cat-card');
  const trigger = card?.querySelector('[data-cloverse-quickview]') || target.closest('[data-cloverse-quickview]');
  if (trigger instanceof HTMLElement && trigger.dataset.productUrl) prefetch(trigger.dataset.productUrl);
}, { passive: true });

document.addEventListener('click', (e) => {
  const trigger = e.target instanceof Element ? e.target.closest('[data-cloverse-quickview]') : null;
  if (!trigger) return;
  e.preventDefault();
  openQuickView(trigger.getAttribute('data-product-url') || '');
});

document.addEventListener('keydown', (e) => {
  if (!modal || modal.hidden) return;
  if (e.key === 'Escape') {
    closeModal();
    return;
  }
  if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
  // Leave the arrow keys alone inside form controls — they move between size
  // radios, which is what a keyboard shopper expects there.
  if (document.activeElement instanceof Element && document.activeElement.closest('input, textarea, select')) return;
  const nav = modal.querySelector(`[data-cqv-nav="${e.key === 'ArrowLeft' ? -1 : 1}"]`);
  if (nav instanceof HTMLElement) {
    e.preventDefault();
    nav.click();
  }
});

// Close the quick view when an add-to-cart succeeds (cart drawer takes over).
// Also drop the prefetch cache: the cached markup carries variant availability,
// and taking the last unit of a size changes what the picker should offer.
document.addEventListener(ThemeEvents.cartUpdate, () => {
  cache.clear();
  if (modal && !modal.hidden) closeModal();
});
